<script setup lang="ts"></script>
<template>
    <ContentPage title="Dichiarazione di Accessibilità Fleequid">
        <section class="mb-8">
            <p class="mt-3 text-base">
                Fleequid (Adorea SRL) si impegna a rendere il proprio sito web accessibile, conformemente alla legge 9 gennaio 2004, n. 4. La presente
                dichiarazione di accessibilità si applica a:
            </p>
            <p class="mt-2">
                <NuxtLink class="text-primary hover:underline" to="https://fleequid.com" target="_blank" external>https://fleequid.com</NuxtLink>
            </p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Accessibilità</h2>
            <p class="mt-2">
                Il nostro obiettivo è rendere i contenuti digitali accessibili a tutte le persone, comprese quelle con disabilità. Ci impegniamo a
                migliorare costantemente l’esperienza d’uso e ad adottare le migliori pratiche in materia di accessibilità, così da garantire un
                accesso equo e inclusivo.
            </p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Dichiarazione di Accessibilità</h2>
            <p class="mt-2">
                In ottemperanza alla normativa vigente e in coerenza con i nostri valori di inclusione e pari opportunità, abbiamo predisposto una
                Dichiarazione di Accessibilità ufficiale. All’interno del documento vengono illustrati il livello di accessibilità del sito e le
                azioni intraprese per rispettare le Web Content Accessibility Guidelines (WCAG). La Dichiarazione completa è disponibile per la
                consultazione e il download tramite il link sottostante:
            </p>
            <p class="mt-2">
                <a class="text-primary hover:underline" href="/docs/accessibility/accessibility-statement_IT.pdf" target="_blank">
                    Dichiarazione di Accessibilità Fleequid
                </a>
            </p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Stato di conformità</h2>
            <p class="mt-2">Parzialmente conforme</p>
            <p class="mt-3">
                Questo sito web è parzialmente conforme ai requisiti previsti dall’appendice A della norma UNI CEI EN 301549 in ragione dei casi di
                non conformità elencati di seguito.
            </p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Contenuti non accessibili</h2>
            <p class="mt-3">I contenuti di seguito elencati non sono accessibili per inosservanza della legge 9 gennaio 2004, n. 4.</p>

            <h3 class="text-highlighted mt-5 text-lg font-semibold">Per le pagine web</h3>
            <h4 class="text-highlighted mt-3 font-medium">A livello generale:</h4>
            <ul class="mt-2 list-disc space-y-1 pl-6">
                <li>1.3.1 Informazioni e correlazioni (Level A)</li>
                <li>1.4.3 Contrasto minimo (Level AA)</li>
                <li>1.4.4 Ridimensionamento del testo (Level AA)</li>
                <li>1.4.10 Ricalcolo del flusso (solo livello AA 2.1)</li>
                <li>2.4.1 Salto di blocchi (Level A)</li>
                <li>2.4.4 Scopo del collegamento (nel contesto) (Level A)</li>
                <li>2.4.5 Differenti modalità (Level AA)</li>
                <li>3.3.1 Identificazione di errori (Level A)</li>
                <li>4.1.2 Nome, ruolo, valore (Level A)</li>
                <li>4.1.3 Messaggi di stato (Level AA, WCAG 2.1)</li>
            </ul>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Redazione della dichiarazione di accessibilità</h2>
            <p class="mt-3">
                La presente dichiarazione è stata redatta il
                <time datetime="2025-08-20" class="font-bold">20 agosto 2025</time>.
            </p>
            <p class="mt-2">
                Le informazioni riportate sono ricavate da una valutazione effettuata da terzi, mediante l’impiego di sistemi di validazione
                automatica e mediante verifica diretta, effettuata manualmente dall’esperto tecnico di accessibilità.
            </p>

            <h3 class="text-highlighted mt-5 text-lg font-semibold">Tool utilizzati per la validazione automatica</h3>
            <ul class="mt-2 list-disc space-y-1 pl-6">
                <li>Deque Axe Accessibility Checker (estensioni di Firefox e Chrome)</li>
                <li>Siteimprove Accessibility Checker</li>
                <li>WCAG color contrast checker</li>
            </ul>

            <h3 class="text-highlighted mt-5 text-lg font-semibold">Tecnologie assistive</h3>
            <ul class="mt-2 list-disc space-y-1 pl-6">
                <li>Screen reader: NVDA</li>
            </ul>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Feedback e informazioni di contatto</h2>
            <p class="mt-3">
                È possibile inviare, in qualunque momento, segnalazioni direttamente all’indirizzo e-mail:
                <FleequidEmailLink type="accessibility" />
            </p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Modalità di invio delle segnalazioni all’AgID</h2>
            <p class="mt-3">
                In caso di risposta insoddisfacente o di mancata risposta, nel termine di trenta giorni, alla notifica o alla richiesta, l’interessato
                può inoltrare una segnalazione utilizzando la modalità indicata nel sito istituzionale di AgID.
            </p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Informazioni sul sito</h2>
            <dl class="mt-4 space-y-4">
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <dt class="font-medium">Data di pubblicazione:</dt>
                    <dd class="sm:col-span-2">
                        <time datetime="2024-08-27" class="font-bold">27 agosto 2024</time>
                    </dd>
                </div>
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <dt class="font-medium">Test di usabilità:</dt>
                    <dd class="font-bold sm:col-span-2">No</dd>
                </div>
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <dt class="font-medium">CMS:</dt>
                    <dd class="font-bold sm:col-span-2">Piattaforma proprietaria/custom sviluppata internamente</dd>
                </div>
            </dl>
        </section>

        <section class="mb-4">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Informazioni sulla struttura</h2>
            <dl class="mt-4 space-y-4">
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <dt class="font-medium">Dipendenti con disabilità:</dt>
                    <dd class="font-bold sm:col-span-2">1</dd>
                </div>
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <dt class="font-medium">Postazioni di lavoro per dipendenti con disabilità:</dt>
                    <dd class="font-bold sm:col-span-2">0</dd>
                </div>
            </dl>
        </section>
    </ContentPage>
</template>
